<template>
    <div role="status" :aria-label="ariaLabel" class="orbiting-particles" :class="{ overlay: overlay }">
        <div ref="orbitContainer" class="orbit">
            <div class="center" />
            <div v-for="color in palette" :key="color" class="wrapper">
                <div class="dot" :style="{ '--dot-color': color }" />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import gsap from "gsap";
import { onMounted, onUnmounted, useTemplateRef } from "vue";

type OrbitingParticlesProps = {
    ariaLabel?: string;
    overlay?: boolean;
};

const { ariaLabel = "Loading", overlay = false } = defineProps<OrbitingParticlesProps>();

const palette = ["#ff2891", "#9c1ab4", "#5a2bd0", "#003fdc"];

const orbitContainer = useTemplateRef<HTMLElement>("orbitContainer");
let mm: gsap.MatchMedia | null = null;

onMounted(() => {
    if (!orbitContainer.value) {
        return;
    }
    mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
        const wrappers = orbitContainer.value!.querySelectorAll<HTMLElement>(".wrapper");
        wrappers.forEach((wrapper, i) => {
            const dot = wrapper.querySelector<HTMLElement>(".dot");
            if (!dot) {
                return;
            }
            gsap.set(wrapper, { rotation: i * 90, transformOrigin: "center" });
            gsap.to(wrapper, {
                rotation: i % 2 === 0 ? "+=360" : "-=360",
                duration: 2.4 + i * 0.3,
                repeat: -1,
                ease: "none",
            });
            gsap.to(dot, {
                scale: 1.5,
                opacity: 1,
                duration: 0.9 + i * 0.1,
                repeat: -1,
                yoyo: true,
                ease: "sine.inOut",
                delay: i * 0.15,
            });
        });
    });
});

onUnmounted(() => {
    mm?.revert();
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";

.orbiting-particles {
    --orbit-size: 100px;
    --orbit-radius: 36px;
    --dot-size: 10px;
    --center-size: 18px;
    @apply flex items-center justify-center;
}

.orbiting-particles.overlay {
    @apply fixed inset-0 backdrop-blur-md;
    /* Sits above the Tailwind scale (z-50 = tooltip) so navigation covers
       any modal/popover that's open when the user starts a transition. */
    z-index: 60;
    background: rgba(250, 250, 250, 0.94);
}

.orbit {
    @apply relative;
    width: var(--orbit-size);
    height: var(--orbit-size);
}

.center {
    @apply absolute top-1/2 left-1/2 rounded-full;
    width: var(--center-size);
    height: var(--center-size);
    background: var(--gradient-brand, linear-gradient(125deg, #ff2891, #003fdc));
    transform: translate(-50%, -50%);
    box-shadow: 0 0 24px rgb(from var(--color-valence-primary-500) r g b / 0.4);
}

.wrapper {
    @apply absolute inset-0;
}

.dot {
    @apply absolute rounded-full opacity-50;
    top: calc(50% - var(--dot-size) / 2);
    left: calc(50% + var(--orbit-radius) - var(--dot-size) / 2);
    width: var(--dot-size);
    height: var(--dot-size);
    background: var(--dot-color);
    box-shadow: 0 0 8px var(--dot-color);
}
</style>
